import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '@/components/ui/alert';
import { Loader2, Camera, Upload, ScanLine, X, AlertTriangle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAppDispatch } from '@/features/hooks';
import {
  setSanitizationWarning,
  clearSanitizationWarning,
} from '@/features/receipts/receiptsSlice';
import api, { endpoints } from '@/services/api';
import type { Category } from '@/lib/types';
import type { TransactionFormData } from './TransactionForm';
import ItemsEditor, {
  type EditableReceiptItem,
} from './ItemsEditor';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AiReceiptResult {
  amount: number;        // negative for expense
  type: string;          // "expense"
  description: string;
  date: string;          // ISO 8601
  discountAmount?: number | null;  // bill-level coupon/discount
  category?: string | null;
  notes?: string | null;
  currency?: string;
  status?: string;
  isRecurring?: boolean;
  items?: Array<{
    description: string;
    amount: number;
    quantity?: number;
    discountAmount?: number | null;  // item-level discount
  }>;
}

/**
 * T232-PY-FE-1 — BE-1 contract: when the receipt scanner upstream returns
 * `detail.error === "all_items_invalid"` (raised as upstream 500 by FastAPI),
 * the proxy remaps to a structured 422 with `proxy_mapped: true`.
 * See backend/src/handlers/extensionHandler.ts:367-418.
 */
interface SanitizationDetail {
  error: 'all_items_invalid';
  message: string;
  items_filtered: number;
  suggestion: string;
}

interface ProxyMapped422Response {
  success: false;
  error: string;
  proxy_mapped: true;
  upstream_status: number;
  proxy_status: 422;
  detail: SanitizationDetail;
}

interface ReceiptScannerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  categories: Category[];
  onScanned: (data: Partial<TransactionFormData>) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Map AI receipt result to TransactionForm initial values.
 * - amount is stored positive in the form (type field carries sign semantics).
 * - category is matched by name (case-insensitive substring) to a category ObjectId.
 */
function mapAiToFormData(
  ai: AiReceiptResult,
  categories: Category[],
): Partial<TransactionFormData> {
  let categoryId: string | undefined;

  if (ai.category) {
    const aiName = ai.category.toLowerCase().trim();
    const match = categories.find((c) => {
      const catName = c.name.toLowerCase().trim();
      return catName === aiName || aiName.includes(catName) || catName.includes(aiName);
    });
    categoryId = match?.id;
  }

  // Safely parse date — date-fns format() throws on Invalid Date, which crashes React render
  const parsedDate = ai.date ? new Date(ai.date) : null;
  const safeDate = parsedDate && !isNaN(parsedDate.getTime()) ? parsedDate : new Date();

  return {
    type: (['income', 'expense', 'transfer'].includes(ai.type)
      ? ai.type
      : 'expense') as 'income' | 'expense' | 'transfer',
    // Form expects positive amount; sign is conveyed by type
    amount: Math.abs(ai.amount),
    description: ai.description,
    date: safeDate,
    notes: ai.notes ?? undefined,
    categoryId: categoryId ?? '',
    discountAmount: ai.discountAmount != null ? ai.discountAmount : undefined,
    isRecurring: ai.isRecurring ?? false,
    // Map receipt line items — include all itemSchema fields to satisfy react-hook-form typing
    items: (ai.items ?? []).map((item) => ({
      description: item.description,
      amount: item.amount,
      quantity: item.quantity ?? 1,
      categoryId: '',
      notes: '',
      emoji: '',
      discountAmount: item.discountAmount ?? 0,
    })),
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ReceiptScannerDialog({
  open,
  onOpenChange,
  categories,
  onScanned,
}: ReceiptScannerDialogProps) {
  const { toast } = useToast();
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isScanning, setIsScanning] = useState(false);

  // T232-PY-FE-1 — when BE-1 returns 422 + proxy_mapped: true, hold the
  // sanitization detail and the user-edited items here until Save / Cancel.
  const [sanitizationDetail, setSanitizationDetail] =
    useState<SanitizationDetail | null>(null);
  const [editedItems, setEditedItems] = useState<EditableReceiptItem[]>([]);

  // ---- File handling --------------------------------------------------------

  const applyFile = (file: File) => {
    setSelectedFile(file);
    const objectUrl = URL.createObjectURL(file);
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return objectUrl;
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) applyFile(file);
    // Reset input value so re-selecting the same file triggers onChange again
    e.target.value = '';
  };

  const reset = () => {
    setSelectedFile(null);
    setPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return null;
    });
    setSanitizationDetail(null);
    setEditedItems([]);
    dispatch(clearSanitizationWarning());
  };

  const handleOpenChange = (val: boolean) => {
    if (!val) reset();
    onOpenChange(val);
  };

  // ---- Scan -----------------------------------------------------------------

  const handleScan = async () => {
    if (!selectedFile) return;
    setIsScanning(true);
    // Clear any prior sanitization state — fresh scan.
    setSanitizationDetail(null);
    setEditedItems([]);
    dispatch(clearSanitizationWarning());

    try {
      const body = new FormData();
      body.append('file', selectedFile);

      // Route through backend proxy so the saved OpenRouter API key is injected.
      // The proxy forwards multipart/form-data to the AI service and returns raw ReceiptResult JSON.
      // Timeout extended to 120s — OpenRouter LLM processing can take 15–60 s for complex receipts.
      const response = await api.post<AiReceiptResult>(
        '/extensions/receipt-scanner/proxy/receipt/extract',
        body,
        { timeout: 120_000 },
      );

      const formDefaults = mapAiToFormData(response.data, categories);

      try {
        const receiptBody = new FormData();
        receiptBody.append('file', selectedFile);
        const uploadResponse = await api.post(endpoints.receipts.upload, receiptBody, { timeout: 60_000 });
        const uploaded = (uploadResponse.data as {
          data?: {
            _id?: string;
            id?: string;
            originalFilename?: string;
            filename?: string;
            mimeType?: string;
            mimetype?: string;
            originalSize?: number;
            fileSize?: number;
          };
        })?.data;
        const pendingReceiptId = uploaded?._id ?? uploaded?.id;
        if (pendingReceiptId) {
          formDefaults.pendingReceiptId = pendingReceiptId;
          formDefaults.pendingReceiptFilename = uploaded?.originalFilename ?? uploaded?.filename ?? selectedFile.name;
          formDefaults.pendingReceiptMimeType = uploaded?.mimeType ?? uploaded?.mimetype ?? selectedFile.type;
          formDefaults.pendingReceiptSize = uploaded?.originalSize ?? uploaded?.fileSize ?? selectedFile.size;
        }
      } catch (uploadErr: unknown) {
        const uploadMessage = (uploadErr as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data?.error
          ?? (uploadErr as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? (uploadErr instanceof Error ? uploadErr.message : 'The receipt image was not stored.');
        toast({
          title: 'Receipt upload failed',
          description: uploadMessage,
          variant: 'destructive',
        });
      }

      onScanned(formDefaults);
      handleOpenChange(false);
    } catch (err: unknown) {
      // T232-PY-FE-1 — detect 422 + proxy_mapped contract from BE-1 and switch
      // into the manual-edit UX instead of the generic destructive toast.
      const axiosErr = err as {
        response?: {
          status?: number;
          data?: unknown;
        };
      };
      const status = axiosErr.response?.status;
      const payload = axiosErr.response?.data as Partial<ProxyMapped422Response> | undefined;
      const isProxyMapped422 =
        status === 422 &&
        payload?.proxy_mapped === true &&
        payload?.detail?.error === 'all_items_invalid';

      if (isProxyMapped422 && payload?.detail) {
        const detail = payload.detail;
        setSanitizationDetail(detail);
        setEditedItems([]);
        dispatch(setSanitizationWarning({
          message: detail.message,
          items_filtered: detail.items_filtered,
          suggestion: detail.suggestion,
        }));
        setIsScanning(false);
        return; // Stay in dialog — render warning banner + ItemsEditor.
      }

      // FastAPI 422 returns detail as an array of Pydantic error objects:
      // [{type, loc, msg, input}] — we must convert to string before passing to toast.
      const d = axiosErr.response?.data as
        | { detail?: string | Array<{ msg?: string }>; error?: string; message?: string }
        | undefined;
      const rawDetail = d?.detail;
      const detailStr = Array.isArray(rawDetail)
        ? rawDetail.map((e) => e?.msg ?? JSON.stringify(e)).join('; ')
        : typeof rawDetail === 'string'
        ? rawDetail
        : undefined;
      const message =
        detailStr ?? d?.error ?? d?.message ??
        (err instanceof Error ? err.message : 'Could not extract data from the receipt. Please try again.');
      toast({
        title: 'Receipt scan failed',
        description: message,
        variant: 'destructive',
      });
    } finally {
      setIsScanning(false);
    }
  };

  // T232-PY-FE-1 — Persist the user-edited items via the existing transaction
  // creation flow. We attach a minimal form defaults envelope (type=expense,
  // no preset amount) because every value will be supplied by the user in the
  // TransactionForm that opens next.
  const handleSaveEdited = async () => {
    if (!selectedFile) {
      toast({
        title: 'Cannot save transaction',
        description: 'Receipt file is no longer attached. Please re-scan.',
        variant: 'destructive',
      });
      return;
    }
    setIsScanning(true);
    try {
      // Best-effort upload so the receipt gets attached to the new transaction.
      let pendingReceiptId: string | undefined;
      try {
        const receiptBody = new FormData();
        receiptBody.append('file', selectedFile);
        const uploadResponse = await api.post(endpoints.receipts.upload, receiptBody, { timeout: 60_000 });
        const uploaded = (uploadResponse.data as {
          data?: {
            _id?: string;
            id?: string;
            originalFilename?: string;
            filename?: string;
            mimeType?: string;
            mimetype?: string;
            originalSize?: number;
            fileSize?: number;
          };
        })?.data;
        pendingReceiptId = uploaded?._id ?? uploaded?.id;
        const formDefaults: Partial<TransactionFormData> = {
          type: 'expense',
          items: editedItems.map((it) => ({
            description: it.description,
            amount: it.amount,
            quantity: it.quantity ?? 1,
            categoryId: '',
            notes: '',
            emoji: '',
            discountAmount: it.discountAmount ?? 0,
          })),
        };
        if (pendingReceiptId) {
          formDefaults.pendingReceiptId = pendingReceiptId;
          formDefaults.pendingReceiptFilename =
            uploaded?.originalFilename ?? uploaded?.filename ?? selectedFile.name;
          formDefaults.pendingReceiptMimeType =
            uploaded?.mimeType ?? uploaded?.mimetype ?? selectedFile.type;
          formDefaults.pendingReceiptSize =
            uploaded?.originalSize ?? uploaded?.fileSize ?? selectedFile.size;
        }
        onScanned(formDefaults);
        handleOpenChange(false);
      } catch (uploadErr: unknown) {
        const uploadMessage = (uploadErr as { response?: { data?: { error?: string; message?: string } } })
          ?.response?.data?.error
          ?? (uploadErr as { response?: { data?: { message?: string } } })?.response?.data?.message
          ?? (uploadErr instanceof Error ? uploadErr.message : 'The receipt image was not stored.');
        toast({
          title: 'Receipt upload failed',
          description: uploadMessage,
          variant: 'destructive',
        });
      }
    } finally {
      setIsScanning(false);
    }
  };

  // ---- Drag & drop ----------------------------------------------------------

  const handleDragOver = (e: React.DragEvent) => e.preventDefault();

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) applyFile(file);
  };

  // ---- Render ---------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="receipt-scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" />
            Scan Receipt
          </DialogTitle>
          <DialogDescription>
            Upload or take a photo of a receipt to auto-fill a new transaction.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* T232-PY-FE-1 — Warning banner for sanitized scan (422 + proxy_mapped).
              Rendered in-place after the scan returns; user can edit items below
              and save, or cancel to re-scan. */}
          {sanitizationDetail && (
            <Alert
              variant="default"
              data-testid="receipt-sanitized-warning"
              className="border-amber-500/60 bg-amber-50 text-amber-900 [&>svg]:text-amber-600 dark:bg-amber-950/40 dark:text-amber-100"
            >
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Items filtered by scanner</AlertTitle>
              <AlertDescription>
                {sanitizationDetail.items_filtered} item
                {sanitizationDetail.items_filtered === 1 ? '' : 's'} had zero or
                negative amount (likely tax/total rows). Review and edit below
                before saving.
                {sanitizationDetail.suggestion === 're-scan' ? (
                  <>
                    {' '}Or cancel and re-scan with a clearer photo.
                  </>
                ) : null}
              </AlertDescription>
            </Alert>
          )}

          {/* Image preview */}
          {preview ? (
            <div className="relative rounded-lg overflow-hidden border bg-muted flex items-center justify-center max-h-72">
              <img
                src={preview}
                alt="Receipt preview"
                className="max-h-72 max-w-full object-contain"
              />
              <button
                type="button"
                onClick={reset}
                className="absolute top-2 right-2 rounded-full bg-background/80 border p-1 hover:bg-background transition-colors"
                aria-label="Remove image"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ) : (
            /* Drop zone */
            <div
              role="button"
              tabIndex={0}
              className="border-2 border-dashed rounded-lg p-8 text-center text-muted-foreground cursor-pointer hover:border-primary hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => e.key === 'Enter' && fileInputRef.current?.click()}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              <Upload className="mx-auto h-10 w-10 mb-3 opacity-50" />
              <p className="text-sm font-medium">Drop image here or click to upload</p>
              <p className="text-xs mt-1 opacity-60">JPEG, PNG, WebP — max 20 MB</p>
            </div>
          )}

          {/* Hidden file inputs */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            className="hidden"
            onChange={handleInputChange}
          />
          {/* capture="environment" → rear camera on mobile */}
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={handleInputChange}
          />

          {/* Upload / Camera buttons — only shown before image is selected */}
          {!preview && (
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                type="button"
                className="min-h-[44px]"
                onClick={() => fileInputRef.current?.click()}
              >
                <Upload className="h-4 w-4 mr-2" />
                Upload File
              </Button>
              <Button
                variant="outline"
                type="button"
                className="min-h-[44px]"
                onClick={() => cameraInputRef.current?.click()}
              >
                <Camera className="h-4 w-4 mr-2" />
                Camera
              </Button>
            </div>
          )}

          {/* T232-PY-FE-1 — Manual-edit form for sanitized items.
              Visible only when a sanitization warning is active. The Save
              button persists items via the existing transaction create flow. */}
          {sanitizationDetail && (
            <div className="space-y-3 pt-2">
              <ItemsEditor
                items={editedItems}
                onChange={setEditedItems}
                caption={`Cleaned items (${editedItems.length})`}
                disabled={isScanning}
              />
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
<Button
variant="ghost"
type="button"
onClick={() => handleOpenChange(false)}
            disabled={isScanning}
            className="min-h-[44px]"
>
            {sanitizationDetail ? 'Cancel (re-scan)' : 'Cancel'}
          </Button>
          {sanitizationDetail ? (
            <Button
              type="button"
              onClick={handleSaveEdited}
              disabled={isScanning || editedItems.length === 0}
              className="min-h-[44px]"
              data-testid="receipt-sanitized-save"
            >
              {isScanning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving…
                </>
              ) : (
                <>
                  <ScanLine className="h-4 w-4 mr-2" />
                  Save Transaction
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleScan}
              disabled={!selectedFile || isScanning}
              className="min-h-[44px]"
            >
              {isScanning ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Scanning…
                </>
              ) : (
                <>
                  <ScanLine className="h-4 w-4 mr-2" />
                  Scan Receipt
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}